// src/utils/safeRedirect.ts
/**
 * Guards against open redirects.
 *
 * Return-URLs that reach us as query parameters are attacker-controllable, so
 * they may never be handed to `window.location` unchecked. Only same-origin
 * targets survive; everything else has to fall back to an app-internal route.
 */

/**
 * Normalises an untrusted URL to a same-origin path (`/pfad?query#hash`).
 *
 * Returns `null` for foreign origins, protocol-relative URLs (`//evil.example`),
 * non-http(s) schemes (`javascript:`) and anything unparsable.
 */
export function toSameOriginPath(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null

  // `//evil.example` inherits the current protocol and would leave the origin.
  if (rawUrl.startsWith('//') || rawUrl.startsWith('\\\\')) return null

  try {
    const origin = window.location.origin
    const url = new URL(rawUrl, origin)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    if (url.origin !== origin) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

/**
 * STORY-1023: Resolves the `next_url` of a magic-link verify response to an
 * in-app navigation target.
 *
 * Only relative paths are accepted — absolute URLs (even same-origin) and
 * protocol-relative targets fall back to the dashboard.
 */
export function safeNext(next: string | null | undefined, fallback = '/dashboard'): string {
  if (!next || !next.startsWith('/')) return fallback
  return toSameOriginPath(next) ?? fallback
}
